import { useEffect, useId, useRef, useState } from 'react';

/**
 * A toolbar menu-button: `Label: Value ▾` opening a checkable popover.
 *
 * This is the filter grammar of modern SaaS toolbars (one row, uniform
 * controls, current value readable on the closed button) — replacing the
 * mixed sprawl of chip rows and labelled native selects. The popover is a
 * real listbox: full keyboard path (open with Enter/arrows, walk with
 * arrows, Esc closes), aria wiring, click-outside dismissal. It reimplements
 * the select's semantics deliberately BECAUSE it adds what a native select
 * cannot: per-option metadata (each family member's total, right-aligned)
 * and a consistent look across platforms.
 */
export interface MenuOption {
  value: string;
  label: string;
  /** Right-aligned secondary text — a member's total, a count. */
  meta?: string;
}

export function MenuSelect({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: MenuOption[];
  onChange: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const listId = useId();
  const current = options.find((o) => o.value === value) ?? options[0];

  useEffect(() => {
    if (!open) return;
    setActive(Math.max(0, options.findIndex((o) => o.value === value)));
    const onDown = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [open, options, value]);

  function pick(v: string) {
    setOpen(false);
    if (v !== value) onChange(v);
  }

  function onKey(e: React.KeyboardEvent) {
    if (e.key === 'Escape') setOpen(false);
    else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) setOpen(true);
      else setActive((a) => Math.min(a + 1, options.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (open && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      const opt = options[active];
      if (opt) pick(opt.value);
    }
  }

  return (
    <div className="menusel" ref={rootRef} onKeyDown={onKey}>
      <button
        type="button"
        className="menusel-btn"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-label={label}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="menusel-label">{label}</span>
        <span className="menusel-value">{current?.label}</span>
        <svg viewBox="0 0 10 6" width="10" height="6" aria-hidden className="menusel-chev">
          <path d="M1 1l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <div className="menusel-pop" role="listbox" id={listId} aria-label={label}>
          {options.map((o, i) => (
            <button
              key={o.value}
              type="button"
              role="option"
              aria-selected={o.value === value}
              className={`menusel-opt${i === active ? ' menusel-opt-active' : ''}`}
              onPointerEnter={() => setActive(i)}
              onClick={() => pick(o.value)}
            >
              <span className="menusel-check" aria-hidden>
                {o.value === value ? '✓' : ''}
              </span>
              <span className="menusel-opt-label">{o.label}</span>
              {o.meta && <span className="menusel-meta">{o.meta}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
